import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Search, Menu, X } from 'lucide-react';

/**
 * Header24 — DHARA Design System
 * Ultra-clean centered logo. Transparent on hero, white on scroll.
 * Nav: Shop, Collections, Journal, About. Green hover accents.
 */
const Header24 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 80);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const leftLinks = [
        { label: 'Shop', path: '/template24/shop' },
        { label: 'Collections', path: '/template24/collections' },
    ];

    const rightLinks = [
        { label: 'Journal', path: '/template24/journal' },
        { label: 'About', path: '/template24/about' },
    ];

    const allLinks = [...leftLinks, ...rightLinks];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-header {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                .dhara-header-inner {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 40px;
                    display: grid;
                    grid-template-columns: 1fr auto 1fr;
                    align-items: center;
                    height: 80px;
                    transition: height 0.4s ease;
                }
                .dhara-header.scrolled .dhara-header-inner {
                    height: 64px;
                }
                .dhara-logo {
                    font-family: 'Playfair Display', serif;
                    font-size: 26px;
                    font-weight: 500;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: opacity 0.3s ease;
                    text-align: center;
                }
                .dhara-logo:hover {
                    opacity: 0.7;
                }
                .dhara-nav-left,
                .dhara-nav-right {
                    display: flex;
                    align-items: center;
                    gap: 36px;
                }
                .dhara-nav-left {
                    justify-content: flex-start;
                }
                .dhara-nav-right {
                    justify-content: flex-end;
                    gap: 28px;
                }
                .dhara-nav-link {
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    letter-spacing: 1.8px;
                    text-transform: uppercase;
                    cursor: pointer;
                    position: relative;
                    padding: 4px 0;
                    transition: color 0.3s ease;
                    background: none;
                    border: none;
                }
                .dhara-nav-link::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    width: 0;
                    height: 1px;
                    background: #00553A;
                    transition: width 0.3s ease;
                }
                .dhara-nav-link:hover {
                    color: #00553A !important;
                }
                .dhara-nav-link:hover::after {
                    width: 100%;
                }
                .dhara-action-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    position: relative;
                    transition: color 0.3s ease;
                }
                .dhara-action-btn:hover {
                    color: #00553A !important;
                }
                .dhara-cart-badge {
                    position: absolute;
                    top: -4px;
                    right: -8px;
                    background: #00553A;
                    color: #FFFFFF;
                    font-family: 'Inter', sans-serif;
                    font-size: 10px;
                    font-weight: 600;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .dhara-mobile-toggle {
                    display: none;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                }
                .dhara-mobile-overlay {
                    position: fixed;
                    inset: 0;
                    background: #FFFFFF;
                    z-index: 999;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 36px;
                }
                .dhara-mobile-link {
                    font-family: 'Playfair Display', serif;
                    font-size: 28px;
                    font-weight: 400;
                    color: #1A1A1A;
                    cursor: pointer;
                    background: none;
                    border: none;
                    transition: color 0.3s ease;
                    letter-spacing: 1px;
                }
                .dhara-mobile-link:hover {
                    color: #00553A;
                }
                .dhara-mobile-close {
                    position: absolute;
                    top: 24px;
                    right: 40px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #1A1A1A;
                }
                .dhara-search-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    background: #FFFFFF;
                    z-index: 1001;
                    padding: 24px 40px;
                    box-shadow: 0 2px 20px rgba(0,0,0,0.06);
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .dhara-search-input {
                    flex: 1;
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    border: none;
                    outline: none;
                    color: #1A1A1A;
                    background: transparent;
                }
                .dhara-search-input::placeholder {
                    color: #6B6B6B;
                }

                @media (max-width: 768px) {
                    .dhara-nav-left,
                    .dhara-nav-right-links { display: none !important; }
                    .dhara-mobile-toggle { display: block !important; }
                    .dhara-header-inner {
                        padding: 0 20px;
                        grid-template-columns: 1fr auto 1fr;
                    }
                    .dhara-logo { font-size: 20px; letter-spacing: 2px; }
                }
            `}</style>

            <header
                className={`dhara-header ${scrolled ? 'scrolled' : ''}`}
                style={{
                    background: scrolled ? 'rgba(255,255,255,0.98)' : 'transparent',
                    borderBottom: scrolled ? '1px solid #E5E5E5' : '1px solid transparent',
                    backdropFilter: scrolled ? 'blur(20px)' : 'none',
                }}
            >
                <div className="dhara-header-inner">
                    {/* Left Nav */}
                    <nav className="dhara-nav-left">
                        {leftLinks.map((link) => (
                            <button
                                key={link.path}
                                className="dhara-nav-link"
                                style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                                onClick={() => handleNavClick(link.path)}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Center Logo */}
                    <div
                        className="dhara-logo"
                        style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                        onClick={() => navigate('/template24')}
                    >
                        DHARA
                    </div>

                    {/* Right Nav + Actions */}
                    <div className="dhara-nav-right">
                        <div className="dhara-nav-right-links" style={{ display: 'flex', gap: '36px', alignItems: 'center' }}>
                            {rightLinks.map((link) => (
                                <button
                                    key={link.path}
                                    className="dhara-nav-link"
                                    style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                                    onClick={() => handleNavClick(link.path)}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <button
                                className="dhara-action-btn"
                                style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                                onClick={() => setSearchOpen(true)}
                            >
                                <Search size={18} strokeWidth={1.5} />
                            </button>
                            <button
                                className="dhara-action-btn"
                                style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                                onClick={() => navigate('/template24/profile')}
                            >
                                <User size={18} strokeWidth={1.5} />
                            </button>
                            <button
                                className="dhara-action-btn"
                                style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                                onClick={() => navigate('/template24/cart')}
                            >
                                <ShoppingBag size={18} strokeWidth={1.5} />
                                {cartCount > 0 && (
                                    <span className="dhara-cart-badge">{cartCount}</span>
                                )}
                            </button>
                            <button
                                className="dhara-mobile-toggle"
                                style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <Menu size={22} strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Search Overlay */}
            {searchOpen && (
                <div className="dhara-search-overlay">
                    <Search size={20} color="#6B6B6B" strokeWidth={1.5} />
                    <input
                        className="dhara-search-input"
                        type="text"
                        placeholder="Search collections, pieces, stories..."
                        autoFocus
                    />
                    <button
                        onClick={() => setSearchOpen(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A1A1A' }}
                    >
                        <X size={20} strokeWidth={1.5} />
                    </button>
                </div>
            )}

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="dhara-mobile-overlay">
                    <button className="dhara-mobile-close" onClick={() => setMobileMenuOpen(false)}>
                        <X size={28} strokeWidth={1.5} />
                    </button>
                    {allLinks.map((link) => (
                        <button
                            key={link.path}
                            className="dhara-mobile-link"
                            onClick={() => handleNavClick(link.path)}
                        >
                            {link.label}
                        </button>
                    ))}
                    <button className="dhara-mobile-link" onClick={() => handleNavClick('/template24/login')}>
                        Account
                    </button>
                </div>
            )}
        </>
    );
};

export default Header24;
